import { useEffect, useRef, useState } from 'react';
import { getLocale, setLocale, translate, supportedLocales } from '../../i18n/index.js';


export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const activeLocale = getLocale();
  const rootRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    function closeOnOutsideClick(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const localeOptions = supportedLocales.map(({ code: locale, name: languageName }) => {
    const isActive = locale === activeLocale;

    return {
      locale,
      isActive,
      languageName,
    };
  });

  return (
    <div
      ref={rootRef}
      className={`af-language-switcher af-language-switcher--dropdown${isOpen ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        className="af-language-switcher-trigger"
        aria-label={translate('components.common.language_switcher.aria_label')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <GlobeIcon />
        <ChevronIcon />
      </button>
      {isOpen ? (
        <div
          className="af-language-switcher-menu"
          role="listbox"
          aria-label={translate('components.common.language_switcher.aria_label')}
        >
          {localeOptions.map(({ locale, isActive, languageName }) => (
            <button
              key={locale}
              type="button"
              className={`af-language-switcher-menu-option${isActive ? ' is-active' : ''}`}
              role="option"
              aria-label={languageName}
              aria-selected={isActive}
              lang={locale}
              onClick={() => setLocale(locale)}
            >
              <span>{languageName}</span>
              {isActive ? <CheckIcon /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2.9 10h14.2M10 2.75c2 2.05 3 4.47 3 7.25s-1 5.2-3 7.25C8 15.2 7 12.78 7 10s1-5.2 3-7.25Z"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      className="af-language-switcher-chevron"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="af-language-switcher-check" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="m3 7 2.5 2.5L11 4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
